import { action, makeObservable, observable, ObservableMap } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { normalizeFilename } from "@/modules/file/normalizeFileName";
import { checkIfFileCanBeUploaded } from "@/modules/file/validation";
import { makeUploadUrlParts } from "@/modules/file/upload";
import { uploadFile } from "@/modules/file/upload";
import { MemCommonEditorFileInfo } from "@mem-labs/common-editor";
import { FileDropItemWithPath } from "@/modules/file/dropItems";
import pLimit from "p-limit";
import axios, { CancelTokenSource } from "axios";

export interface FileUploadProgress {
  filename: string;
  uploadedBytes: number;
  totalBytes: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: Error;
}

export interface FileUploadResult {
  info: MemCommonEditorFileInfo;
  fileItem: FileDropItemWithPath;
  uploadedFileBlobName: string;
  signedUrl: string;
}
export class AppStoreUploadProgressStore extends AppSubStore {
  // Track individual file upload progress
  private fileProgressMap = new ObservableMap<Uuid, FileUploadProgress>();
  private fileResultsMap = new ObservableMap<Uuid, FileUploadResult>();
  // Track cancel sources per session so we can abort all uploads for a session
  private sessionCancelSources = new ObservableMap<Uuid, Set<CancelTokenSource>>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      AppStoreUploadProgressStore,
      "fileProgressMap" | "fileResultsMap" | "sessionCancelSources"
    >(this, {
      cancelUploadsBySession: true,
      getFileProgresses: true,
      getFileResults: true,
      getFileProgress: true,
      getFileResult: true,

      fileProgressMap: observable,
      fileResultsMap: observable,
      sessionCancelSources: observable,

      updateFileProgress: action,
      clearFileProgress: action,
      uploadFileItem: action,
      uploadFileItems: action,
      resetState: action,
    });
  }

  getFileProgress(fileId: Uuid): FileUploadProgress | undefined {
    return this.fileProgressMap.get(fileId);
  }

  getFileResult(fileId: Uuid): FileUploadResult | undefined {
    return this.fileResultsMap.get(fileId);
  }

  getFileProgresses(fileIds: Uuid[]): FileUploadProgress[] {
    return fileIds
      .map(id => {
        return this.getFileProgress(id);
      })
      .filter(Boolean) as FileUploadProgress[];
  }

  getFileResults(fileIds: Uuid[]): FileUploadResult[] {
    return fileIds.map(id => this.getFileResult(id)).filter(Boolean) as FileUploadResult[];
  }

  // File progress methods
  updateFileProgress(fileId: Uuid, progress: Partial<FileUploadProgress>) {
    const currentProgress = this.fileProgressMap.get(fileId) || {
      filename: "",
      uploadedBytes: 0,
      totalBytes: 0,
      status: "pending" as const,
    };

    this.fileProgressMap.set(fileId, {
      ...currentProgress,
      ...progress,
    });
  }

  clearFileProgress(fileId: Uuid) {
    this.fileProgressMap.delete(fileId);
  }

  async uploadFileItem(fileItem: FileDropItemWithPath, sessionId: Uuid): Promise<Uuid> {
    const file = await fileItem.getFile();
    const fileId = uuidModule.generate();

    this.updateFileProgress(fileId, {
      filename: fileItem.name,
      status: "pending",
      uploadedBytes: 0,
      totalBytes: file.size,
    });

    const normalizedFileName = normalizeFilename({ fileName: fileItem.name });
    const info = {
      fileId,
      normalizedFileName,
      fileMimeType: fileItem.type,
      encodedFileContent: file.name, // TODO: write a comment explaining this unexpected key/val
      fileSizeBytes: file.size,
    };

    const canBeUploaded = checkIfFileCanBeUploaded({ info });
    if (!canBeUploaded) {
      throw new Error("File can not be uploaded");
    }

    // Create and record a cancel token for this upload under the session
    const source = axios.CancelToken.source();
    let uploadResult;
    try {
      const cancelSet = this.sessionCancelSources.get(sessionId) || new Set<CancelTokenSource>();
      cancelSet.add(source);
      this.sessionCancelSources.set(sessionId, cancelSet);

      uploadResult = await uploadFile({
        info,
        blob: file,
        config: {
          cancelToken: source.token,
          onUploadProgress: progress => {
            const status: FileUploadProgress["status"] =
              progress.loaded === progress.total ? "completed" : "pending";
            this.updateFileProgress(fileId, {
              uploadedBytes: progress.loaded,
              totalBytes: progress.total,
              status,
            });
          },
        },
      });
    } finally {
      // Remove the source from the session set regardless of outcome
      const cancelSet = this.sessionCancelSources.get(sessionId);
      if (cancelSet) {
        cancelSet.delete(source);
        if (cancelSet.size === 0) {
          this.sessionCancelSources.delete(sessionId);
        } else {
          this.sessionCancelSources.set(sessionId, cancelSet);
        }
      }
    }

    const { signedUrl } = uploadResult;
    const urlParts = makeUploadUrlParts(signedUrl);
    if (!urlParts.blobName) {
      throw new Error("Invalid signed url");
    }

    this.fileResultsMap.set(fileId, {
      info,
      fileItem,
      uploadedFileBlobName: urlParts.blobName,
      signedUrl,
    });

    return fileId;
  }

  async uploadFileItems(fileItems: FileDropItemWithPath[], sessionId: Uuid): Promise<Uuid[]> {
    const concurrencyLimit = 10;
    const uploadLimit = pLimit(concurrencyLimit);
    const promises: Promise<Uuid>[] = fileItems.map(fileItem =>
      uploadLimit(() => this.uploadFileItem(fileItem, sessionId))
    );

    return Promise.all(promises);
  }

  /** Abort all in-flight uploads for a given session */
  cancelUploadsBySession(sessionId: Uuid) {
    const cancelSet = this.sessionCancelSources.get(sessionId);
    if (!cancelSet || cancelSet.size === 0) return;
    for (const source of cancelSet) {
      try {
        source.cancel("canceled-by-user");
      } catch {
        // noop
      }
    }
    this.sessionCancelSources.delete(sessionId);
  }

  // Reset all state
  resetState() {
    this.fileProgressMap.clear();
    this.fileResultsMap.clear();
    this.sessionCancelSources.clear();
  }
}
